import React, { Fragment, useState, useEffect, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import { Row, Col, Divider, Card, Button } from 'antd';
import MemberGroup from './MemberGroup';
import FilterGroup from './FilterGroup';
import TimeGroup from './TimeGroup';
import SelectChartType from './SelectChartType';
import ChartRenderer from '../ChartRenderer';
import LoadingSpinner from '../LoadingSpinner';
import { useDeepCompareEffect } from 'use-deep-compare';
import { firebaseAuth } from '../../../firebase';

// Helper to convert member objects to query format
const memberToQueryMember = (member) => member.name;

// Strip view/cube name from title so only field name is shown (e.g. "Transactions view Transactionid" -> "Transactionid")
const fieldTitleOnly = (memberTitle, cube) => {
  if (!memberTitle) return memberTitle;
  const viewLabel = cube?.title || (cube?.name || '').replace(/_/g, ' ');
  if (!viewLabel) return memberTitle;
  const prefix = viewLabel.trim() + ' ';
  return memberTitle.startsWith(prefix) ? memberTitle.slice(prefix.length) : memberTitle;
};

// Helper to convert timeDimension to query format
const timeDimensionToQuery = (td) => ({
  dimension: td.dimension.name,
  granularity: td.granularity,
  dateRange: td.dateRange || undefined,
});

// Helper to convert filter to query format
const filterToQuery = (f) => {
  // Handle different dimension formats: could be object with .name, or string
  let dimensionName;
  if (typeof f.dimension === 'string') {
    dimensionName = f.dimension;
  } else if (f.dimension?.name) {
    dimensionName = f.dimension.name;
  } else if (typeof f.dimension === 'object' && f.dimension !== null) {
    // Try to get name from various possible properties
    dimensionName = f.dimension.name || f.dimension.title || f.dimension.key;
  } else {
    console.warn('Filter dimension is not in expected format:', f.dimension);
    dimensionName = String(f.dimension);
  }

  // Handle both array values and single value
  const values = f.values && Array.isArray(f.values) && f.values.length > 0
    ? f.values
    : (f.value !== undefined && f.value !== null && f.value !== '' ? [f.value] : []);

  return {
    member: dimensionName,
    operator: f.operator,
    values: values,
  };
};

// Operators list for filters by member type (used when adding and when restoring from vizState)
const getOperatorsForMemberType = (memberType) => {
  const isDateField = memberType === 'time';
  if (memberType === 'string') {
    return [
      { name: 'equals', title: 'equals' },
      { name: 'notEquals', title: 'not equals' },
      { name: 'contains', title: 'contains' },
      { name: 'notContains', title: 'not contains' },
    ];
  }
  if (isDateField) {
    return [
      { name: 'equals', title: 'on' },
      { name: 'notEquals', title: 'not on' },
      { name: 'gt', title: 'after' },
      { name: 'gte', title: 'on or after' },
      { name: 'lt', title: 'before' },
      { name: 'lte', title: 'on or before' },
    ];
  }
  return [
    { name: 'equals', title: 'equals' },
    { name: 'notEquals', title: 'not equals' },
    { name: 'gt', title: 'greater than' },
    { name: 'gte', title: 'greater than or equal' },
    { name: 'lt', title: 'less than' },
    { name: 'lte', title: 'less than or equal' },
  ];
};

const today = new Date();

const ViewToggle = ({ viewMode, setViewMode }) => (
  <div 
    className="d-flex rounded-pill p-1 mb-4 position-relative" 
    style={{ 
      width: '320px', 
      backgroundColor: '#E6E6E6', 
      zIndex: 0 
    }}
  >
    <div 
      className="rounded-pill position-absolute"
      style={{
        transition: 'transform 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
        top: '4px',
        bottom: '4px',
        width: 'calc(50% - 4px)',
        left: '4px',
        backgroundColor: '#FFFFFF',
        transform: viewMode === 'savings_rate' ? 'translateX(0)' : 'translateX(100%)',
        zIndex: -1,
      }}
    />
    <div 
      className={`py-2 rounded-pill text-center ${viewMode === 'savings_rate' ? 'fw-bold' : ''}`}
      onClick={() => setViewMode('savings_rate')}
      style={{ 
        cursor: 'pointer', 
        transition: 'color 0.2s ease', 
        fontSize: '15px', 
        color: viewMode === 'savings_rate' ? '#333' : '#555555', 
        flex: 1 
      }}
    >
      Savings
    </div>
    <div 
      className={`py-2 rounded-pill text-center ${viewMode === 'transactions' ? 'fw-bold' : ''}`}
      onClick={() => setViewMode('transactions')}
      style={{ 
        cursor: 'pointer', 
        transition: 'color 0.2s ease', 
        fontSize: '15px', 
        color: viewMode === 'transactions' ? '#333' : '#555555', 
        flex: 1 
      }}
    >
      Spending
    </div>
  </div>
);

export default function CustomQueryBuilder({
  vizState,
  cubejsApi,
  chartExtra,
  onVizStateChanged,
  shouldExecute = false,
  onQueryStateChange,
}) {
  // State for metadata
  const [meta, setMeta] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaError, setMetaError] = useState(null);

  // State for view toggle
  const [viewMode, setViewMode] = useState('savings_rate');
  const [hasSetDefault, setHasSetDefault] = useState(false);

  const handleViewModeChange = (mode) => {
    if (mode === viewMode) return;
    setViewMode(mode);
    setHasSetDefault(false);
    setMeasures([]);
    setDimensions([]);
    setSegments([]);
    setTimeDimensions([]);
    setFilters([]);
    setOrderMembers([]);
  };

  const targetCubeName = viewMode === 'transactions' ? 'transactions_view' : 'monthly_transactions';

  // State for query components
  const [measures, setMeasures] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [segments, setSegments] = useState([]);
  const [timeDimensions, setTimeDimensions] = useState([]);
  const [filters, setFilters] = useState([]);
  const [chartType, setChartType] = useState('bar');
  const [orderMembers, setOrderMembers] = useState([]);

  // Load metadata (this is NOT a data query, just gets available fields)
  useEffect(() => {
    if (!cubejsApi) return;

    const uid = firebaseAuth.currentUser?.uid;
    const cacheKey = uid ? `cubejs_meta_${uid}` : 'cubejs_meta_default';

    const cachedMeta = localStorage.getItem(cacheKey);
    if (cachedMeta) {
      try {
        setMeta(JSON.parse(cachedMeta));
        setMetaLoading(false);
      } catch (e) {
        setMetaLoading(true);
      }
    } else {
      setMetaLoading(true);
    }

    setMetaError(null);

    cubejsApi
      .meta()
      .then((metaData) => {
        localStorage.setItem(cacheKey, JSON.stringify(metaData));
        setMeta(metaData);
        setMetaLoading(false);
      })
      .catch((error) => {
        console.error('Error loading metadata:', error, error.stack);
        if (!cachedMeta) {
          setMetaError(error);
          setMetaLoading(false);
        }
      });
  }, [cubejsApi]);

  // Placeholder: filter restoration is done in effect below after availableDimensions/availableMeasures exist

  // Get available members from metadata
  // The meta() method returns an object with cubes array
  const availableMeasures = useMemo(() => {
    if (!meta || !meta.cubes) return [];
    return meta.cubes
      .filter(cube => cube.name === targetCubeName)
      .flatMap(cube => (cube.measures || []).map(measure => ({
        ...measure,
        cubeName: cube.name,
        displayTitle: fieldTitleOnly(measure.title, cube) || measure.title,
      })));
  }, [meta, targetCubeName]);

  const availableDimensions = useMemo(() => {
    if (!meta || !meta.cubes) return [];
    return meta.cubes
      .filter(cube => cube.name === targetCubeName)
      .flatMap(cube => (cube.dimensions || []).map(dimension => ({
        ...dimension,
        cubeName: cube.name,
        granularities: dimension.granularities || [],
        displayTitle: fieldTitleOnly(dimension.title, cube) || dimension.title,
      })));
  }, [meta, targetCubeName]);

  const availableSegments = useMemo(() => {
    if (!meta || !meta.cubes) return [];
    return meta.cubes
      .filter(cube => cube.name === targetCubeName)
      .flatMap(cube => (cube.segments || []).map(segment => ({
        ...segment,
        cubeName: cube.name,
        displayTitle: fieldTitleOnly(segment.title, cube) || segment.title,
      })));
  }, [meta, targetCubeName]);

  const availableTimeDimensions = useMemo(() => {
    // Time dimensions are typically dimensions with time granularities
    return availableDimensions.filter(d => d.granularities && d.granularities.length > 0);
  }, [availableDimensions]);

  // Initialize from vizState (runs after available* so we can resolve filter dimensions to display names + operators)
  useDeepCompareEffect(() => {
    if (!vizState?.query) return;
    const query = vizState.query;
    if (query.measures) setMeasures(query.measures.map(name => ({ name, title: name })));
    if (query.dimensions) setDimensions(query.dimensions.map(name => ({ name, title: name })));
    if (query.segments) setSegments(query.segments.map(name => ({ name, title: name })));
    if (query.timeDimensions) {
      setTimeDimensions(query.timeDimensions.map(td => ({
        dimension: { name: td.dimension, title: td.dimension },
        granularity: td.granularity,
        dateRange: td.dateRange,
      })));
    }
    if (query.filters && query.filters.length > 0) {
      const allFilterable = [...(availableDimensions || []), ...(availableMeasures || [])];
      const resolvedFilters = query.filters.map((f, i) => {
        const resolved = allFilterable.find(m => m.name === f.member);
        const dimension = resolved || { name: f.member, title: f.member };
        const memberType = dimension.type || 'string';
        // If dimension not resolved yet (e.g. meta loading), infer time type when operator is date-like
        const inferredType = memberType === 'string' && ['gt', 'gte', 'lt', 'lte'].includes(f.operator) ? 'time' : memberType;
        const operators = getOperatorsForMemberType(resolved ? memberType : inferredType);
        const operator = operators.some(op => op.name === f.operator) ? f.operator : (operators[0]?.name || 'equals');
        return {
          dimension,
          operator,
          values: f.values || [],
          operators,
          index: i,
        };
      });
      setFilters(resolvedFilters);
    } else if (query.filters && query.filters.length === 0) {
      setFilters([]);
    }
    if (vizState.chartType != null) setChartType(vizState.chartType);
  }, [vizState, availableDimensions, availableMeasures]);

  // Set default query if none exists
  useEffect(() => {
    if (!hasSetDefault && availableMeasures.length > 0 && availableDimensions.length > 0) {
      if (!vizState?.query || Object.keys(vizState.query).length === 0 || measures.length === 0) {
        let amountMeasure;
        let categoryDim;
        
        if (viewMode === 'transactions') {
           amountMeasure = availableMeasures.find(m => m.name.toLowerCase().includes('amount'));
           categoryDim = availableDimensions.find(d => d.name.toLowerCase().includes('category'));
        } else {
           amountMeasure = availableMeasures.find(m => m.name.toLowerCase().includes('savings_rate') || m.name.toLowerCase().includes('amount') || m.name.toLowerCase().includes('rate'));
           categoryDim = availableDimensions.find(d => d.name.toLowerCase().includes('month') || d.name.toLowerCase().includes('category'));
        }
        
        const timeDim = availableTimeDimensions.find(d => d.name.toLowerCase().includes('occurred') || d.name.toLowerCase().includes('date') || d.name.toLowerCase().includes('time')) || availableTimeDimensions[0];

        if (amountMeasure) setMeasures([{ name: amountMeasure.name, title: amountMeasure.title || amountMeasure.name }]);
        if (categoryDim) setDimensions([{ name: categoryDim.name, title: categoryDim.title || categoryDim.name }]);
        if (timeDim) {
          setTimeDimensions([{
            dimension: { name: timeDim.name, title: timeDim.title || timeDim.name },
            granularity: viewMode === 'savings_rate' ? 'month' : 'day',
            dateRange: viewMode === 'savings_rate' ? 'last 12 months' : 'last 30 days',
          }]);
        }
      }
      setHasSetDefault(true);
    }
  }, [hasSetDefault, viewMode, availableMeasures, availableDimensions, availableTimeDimensions, vizState, measures.length]);

  // Build query object from state
  const query = useMemo(() => {
    const queryObj = {};

    if (measures.length > 0) {
      queryObj.measures = measures.map(memberToQueryMember);
    }
    if (dimensions.length > 0) {
      queryObj.dimensions = dimensions.map(memberToQueryMember);
    }
    if (segments.length > 0) {
      queryObj.segments = segments.map(memberToQueryMember);
    }
    if (timeDimensions.length > 0) {
      queryObj.timeDimensions = timeDimensions.map(timeDimensionToQuery);
    }
    if (filters.length > 0) {
      const validFilters = filters.filter(f => {
        // Filter must have dimension with a name, operator, and non-empty values
        if (!f.dimension) return false;
        const dimensionName = f.dimension.name || f.dimension;
        if (!dimensionName) return false;
        if (!f.operator) return false;
        // Check if values array has content (for array-based filters)
        const hasValues = f.values && Array.isArray(f.values) && f.values.length > 0;
        // Check if single value exists (for single-value filters)
        const hasValue = f.value !== undefined && f.value !== null && f.value !== '';
        return hasValues || hasValue;
      });

      if (validFilters.length > 0) {
        queryObj.filters = validFilters.map(filterToQuery);
        console.log('Filters added to query:', queryObj.filters);
      }
    }
    if (orderMembers.length > 0) {
      queryObj.order = orderMembers;
    } else {
      const defaultOrder = [];
      if (dimensions.length > 0) {
        dimensions.forEach(d => {
          defaultOrder.push([memberToQueryMember(d), 'asc']);
        });
      }
      if (timeDimensions.length > 0) {
        timeDimensions.forEach(td => {
          defaultOrder.push([td.dimension.name, 'asc']);
        });
      }
      if (defaultOrder.length > 0) {
        queryObj.order = defaultOrder;
      }
    }

    return Object.keys(queryObj).length > 0 ? queryObj : null;
  }, [measures, dimensions, segments, timeDimensions, filters, orderMembers]);

  // Debug: Log filters and query when they change
  useEffect(() => {
    console.log('Filters state:', filters);
    console.log('Query object:', query);
  }, [filters, query]);

  const isQueryPresent = query !== null && (measures.length > 0 || dimensions.length > 0 || timeDimensions.length > 0);

  // Notify parent of state changes
  useEffect(() => {
    if (onVizStateChanged) {
      onVizStateChanged({
        query,
        chartType,
      });
    }
  }, [query, chartType, onVizStateChanged]);

  // Update methods for measures
  const measureMethods = {
    add: (member) => {
      setMeasures([...measures, { ...member, index: measures.length }]);
    },
    update: (member, updateWith) => {
      setMeasures(measures.map(m => m.index === member.index ? updateWith : m));
    },
    remove: (member) => {
      setMeasures(measures.filter(m => m.index !== member.index));
    },
  };

  // Update methods for dimensions
  const dimensionMethods = {
    add: (member) => {
      setDimensions([...dimensions, { ...member, index: dimensions.length }]);
    },
    update: (member, updateWith) => {
      setDimensions(dimensions.map(m => m.index === member.index ? updateWith : m));
    },
    remove: (member) => {
      setDimensions(dimensions.filter(m => m.index !== member.index));
    },
  };

  // Update methods for segments
  const segmentMethods = {
    add: (member) => {
      setSegments([...segments, { ...member, index: segments.length }]);
    },
    update: (member, updateWith) => {
      setSegments(segments.map(m => m.index === member.index ? updateWith : m));
    },
    remove: (member) => {
      setSegments(segments.filter(m => m.index !== member.index));
    },
  };

  // Update methods for time dimensions
  const timeDimensionMethods = {
    add: (member) => {
      setTimeDimensions([...timeDimensions, {
        dimension: member,
        granularity: 'day',
        dateRange: undefined,
      }]);
    },
    update: (member, updateWith) => {
      setTimeDimensions(timeDimensions.map(td =>
        td.dimension.name === member.dimension.name ? updateWith : td
      ));
    },
    remove: (member) => {
      setTimeDimensions(timeDimensions.filter(td =>
        td.dimension.name !== member.dimension.name
      ));
    },
  };

  // Update methods for filters
  const filterMethods = {
    add: (memberOrFilter) => {
      // Handle both cases: member object directly, or filter object with dimension/operator/values
      const actualMember = memberOrFilter.dimension || memberOrFilter;
      const existingOperator = memberOrFilter.operator;
      const existingValues = memberOrFilter.values;

      const memberType = actualMember.type || actualMember.dimension?.type || 'string';
      const operators = getOperatorsForMemberType(memberType);

      const newFilter = {
        dimension: actualMember,
        operator: existingOperator || operators[0].name,
        operators: operators,
        values: existingValues || [],
        index: filters.length,
      };

      console.log('Adding filter:', newFilter);
      setFilters([...filters, newFilter]);
    },
    update: (filter, updateWith) => {
      console.log('Updating filter:', filter, 'with:', updateWith);
      setFilters(filters.map(f => f.index === filter.index ? updateWith : f));
    },
    remove: (filter) => {
      setFilters(filters.filter(f => f.index !== filter.index));
    },
  };

  // Update methods for order
  const orderMethods = {
    set: (order) => {
      setOrderMembers(order);
    },
    reorder: (order) => {
      setOrderMembers(order);
    },
  };

  if (metaLoading) {
    return <LoadingSpinner />;
  }

  if (metaError) {
    return <div>Error loading metadata: {metaError.toString()}</div>;
  }

  return (
    <Fragment>
      <Row
        type="flex"
        justify="space-around"
        align="top"
        gutter={24}
        style={{
          marginBottom: 12,
        }}
      >
        <Col span={24}>
          <div className='d-block d-lg-none bg-white navMonthDate scrollNavMonths css-box-shadow ps-4 pt-4 pt-4 pb-5 mb-4'>
            <div id="currentDay">
              {today.toLocaleString('en-EN', { weekday: 'long' })}
            </div>
            <div id="currentDate" className="mb-4">
              {(today.toLocaleString('en-EN', { month: 'long', day: 'numeric', year: 'numeric' })).replace(',', '')}
            </div>

            <ViewToggle viewMode={viewMode} setViewMode={handleViewModeChange} />

            <Row
              type="flex"
              justify="space-around"
              align="top"
              gutter={24}
              style={{
                marginBottom: 12,
              }}
            >
              <Col span={24}>
                <MemberGroup
                  members={dimensions}
                  availableMembers={availableDimensions}
                  addMemberName="Dimension"
                  updateMethods={dimensionMethods}
                  meta={meta}
                />
                <Divider type="vertical" />
                <MemberGroup
                  members={measures}
                  availableMembers={availableMeasures}
                  addMemberName="Measure"
                  updateMethods={measureMethods}
                  meta={meta}
                />
                {/* 
                <Divider type="vertical" />
                <MemberGroup
                  members={segments}
                  availableMembers={availableSegments}
                  addMemberName="Segment"
                  updateMethods={segmentMethods}
                  meta={meta}
                />
                <Divider type="vertical" />
                <TimeGroup
                  members={timeDimensions}
                  availableMembers={availableTimeDimensions}
                  addMemberName="Time"
                  updateMethods={timeDimensionMethods}
                />
                */}
              </Col>
            </Row>

            <Row
              type="flex"
              justify="space-around"
              align="top"
              gutter={24}
              style={{
                marginBottom: 12,
              }}
            >
              <Col span={24}>
                <FilterGroup
                  members={filters}
                  availableMembers={availableDimensions.concat(availableMeasures)}
                  addMemberName="Filter"
                  updateMethods={filterMethods}
                />
              </Col>
            </Row>

            <Row
              type="flex"
              justify="space-around"
              align="top"
              gutter={24}
            >
              <Col span={24}>
                <SelectChartType
                  chartType={chartType}
                  updateChartType={setChartType}
                />

                <Divider type="vertical" />

                {chartExtra && chartExtra.map((button, index) => {
                  const currentDisabled = button.props?.disabled;
                  const isDisabled = currentDisabled || !isQueryPresent;
                  return React.cloneElement(button, {
                    key: button.key || index,
                    disabled: isDisabled,
                    ...(isDisabled && {
                      style: {
                        backgroundColor: '#f5f5f5',
                        borderColor: '#d9d9d9',
                        color: 'rgba(0, 0, 0, 0.25)',
                        ...button.props.style,
                        backgroundColor: '#d9d9d9',
                        borderColor: '#d9d9d9',
                      }
                    })
                  });
                })}
              </Col>
            </Row>
          </div>

          <div className='d-none d-lg-block me-lg-5'>
            <ViewToggle viewMode={viewMode} setViewMode={handleViewModeChange} />

            <Row
              type="flex"
              justify="space-around"
              align="top"
              gutter={24}
              style={{
                marginBottom: 12,
              }}
            >
              <Col span={24}>
                <MemberGroup
                  members={dimensions}
                  availableMembers={availableDimensions}
                  addMemberName="Dimension"
                  updateMethods={dimensionMethods}
                  meta={meta}
                />
                <Divider type="vertical" />
                <MemberGroup
                  members={measures}
                  availableMembers={availableMeasures}
                  addMemberName="Measure"
                  updateMethods={measureMethods}
                  meta={meta}
                />
              </Col>
            </Row>

            <Row
              type="flex"
              justify="space-around"
              align="top"
              gutter={24}
              style={{
                marginBottom: 12,
              }}
            >
              <Col span={24}>
                <FilterGroup
                  members={filters}
                  availableMembers={availableDimensions.concat(availableMeasures)}
                  addMemberName="Filter"
                  updateMethods={filterMethods}
                />
              </Col>
            </Row>

            <Row
              type="flex"
              justify="space-around"
              align="top"
              gutter={24}
            >
              <Col span={24}>
                <SelectChartType
                  chartType={chartType}
                  updateChartType={setChartType}
                />

                <Divider type="vertical" />

                {chartExtra && chartExtra.map((button, index) => {
                  const currentDisabled = button.props?.disabled;
                  const isDisabled = currentDisabled || !isQueryPresent;
                  return React.cloneElement(button, {
                    key: button.key || index,
                    disabled: isDisabled,
                    ...(isDisabled && {
                      style: {
                        backgroundColor: '#f5f5f5',
                        borderColor: '#d9d9d9',
                        color: 'rgba(0, 0, 0, 0.25)',
                        ...button.props.style,
                        backgroundColor: '#d9d9d9',
                        borderColor: '#d9d9d9',
                      }
                    })
                  });
                })}
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      <Row type="flex" justify="space-around" align="top" gutter={24} className="me-lg-5">
        <Col span={24}>
          {isQueryPresent ? (
            <Card
              style={{
                minHeight: 420,
              }}
            >
              {shouldExecute ? (
                <ChartRenderer
                  vizState={{
                    query,
                    chartType,
                  }}
                  cubejsApi={cubejsApi}
                  shouldExecute={shouldExecute}
                  onQueryStateChange={onQueryStateChange}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                  <p>Click "Run Query" to execute the chart</p>
                </div>
              )}
            </Card>
          ) : (
            <h2
              style={{
                textAlign: 'center',
              }}
            >
              Choose a measure or dimension to get started
            </h2>
          )}
        </Col>
      </Row>
    </Fragment>
  );
}

CustomQueryBuilder.propTypes = {
  vizState: PropTypes.object,
  cubejsApi: PropTypes.object,
  chartExtra: PropTypes.array,
  shouldExecute: PropTypes.bool,
  onQueryStateChange: PropTypes.func,
  onVizStateChanged: PropTypes.func,
};

CustomQueryBuilder.defaultProps = {
  vizState: {},
  cubejsApi: null,
  chartExtra: null,
  shouldExecute: false,
  onQueryStateChange: null,
  onVizStateChanged: null,
};

